import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { Package, Plus, AlertTriangle, MinusCircle, PlusCircle, History, Trash2 } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

const CATEGORIAS = ["cocina", "barra", "snacks", "desechables", "decoracion", "limpieza", "mobiliario", "equipo", "general"];
const UNIDADES = ["unidad", "kg", "g", "l", "ml", "pieza", "paquete", "caja"];
const TIPOS_MOV = [
  { value: "entrada", label: "Entrada (compra)" },
  { value: "salida", label: "Salida (uso/evento)" },
  { value: "merma", label: "Merma (pérdida)" },
  { value: "ajuste", label: "Ajuste (set stock)" },
];

const emptyItem = { nombre: "", categoria: "general", unidad: "unidad", stock_actual: 0, stock_minimo: 0, costo_promedio: 0, ubicacion_id: "", notas: "" };
const emptyMov = { tipo: "entrada", cantidad: 0, costo_unitario: 0, fecha: new Date().toISOString().slice(0, 10), motivo: "" };

export default function InventarioTab({ unidadSlug, color }) {
  const [items, setItems] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dialogItemOpen, setDialogItemOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formItem, setFormItem] = useState(emptyItem);

  const [dialogMovOpen, setDialogMovOpen] = useState(false);
  const [itemForMov, setItemForMov] = useState(null);
  const [formMov, setFormMov] = useState(emptyMov);

  const [dialogHistOpen, setDialogHistOpen] = useState(false);
  const [histItem, setHistItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = `?unidad_negocio=${unidadSlug}`;
      const [r, res, u] = await Promise.all([
        axios.get(`${API}/inventario/items${q}`),
        axios.get(`${API}/inventario/resumen${q}`).catch(() => ({ data: null })),
        axios.get(`${API}/ubicaciones${q}`).catch(() => ({ data: [] })),
      ]);
      setItems(r.data || []);
      setResumen(res.data || null);
      setUbicaciones(u.data || []);
    } catch { toast.error("Error al cargar inventario"); }
    finally { setLoading(false); }
  }, [unidadSlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveItem = async () => {
    if (!formItem.nombre?.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = {
        ...formItem,
        stock_actual: Number(formItem.stock_actual) || 0,
        stock_minimo: Number(formItem.stock_minimo) || 0,
        costo_promedio: Number(formItem.costo_promedio) || 0,
        unidad_negocio: unidadSlug,
        ubicacion_id: formItem.ubicacion_id || null,
      };
      if (editing) {
        await axios.put(`${API}/inventario/items/${editing.item_id}`, payload);
        toast.success("Item actualizado");
      } else {
        await axios.post(`${API}/inventario/items`, payload);
        toast.success("Item creado");
      }
      setDialogItemOpen(false);
      setEditing(null);
      setFormItem(emptyItem);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Eliminar este item?")) return;
    try {
      await axios.delete(`${API}/inventario/items/${id}`);
      toast.success("Item eliminado");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleSaveMov = async () => {
    if (!itemForMov) return;
    const cant = Number(formMov.cantidad);
    if (!cant || cant <= 0) { toast.error("Cantidad debe ser > 0"); return; }
    try {
      await axios.post(`${API}/inventario/movimientos`, {
        ...formMov,
        item_id: itemForMov.item_id,
        cantidad: cant,
        costo_unitario: Number(formMov.costo_unitario) || 0,
      });
      toast.success("Movimiento registrado");
      setDialogMovOpen(false);
      setFormMov(emptyMov);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openMov = (item, tipo = "entrada") => {
    setItemForMov(item);
    setFormMov({ ...emptyMov, tipo, costo_unitario: item.costo_promedio || 0 });
    setDialogMovOpen(true);
  };

  const openHist = async (item) => {
    try {
      const r = await axios.get(`${API}/inventario/items/${item.item_id}`);
      setHistItem(r.data);
      setDialogHistOpen(true);
    } catch { toast.error("Error al cargar historial"); }
  };

  const lowStock = items.filter((i) => (i.stock_actual || 0) <= (i.stock_minimo || 0));
  const ubiName = (id) => ubicaciones.find((u) => u.ubicacion_id === id)?.nombre || "—";

  return (
    <div className="space-y-4" data-testid={`inventario-tab-${unidadSlug}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-stone-900">Inventario</h2>
          <p className="text-stone-500 text-sm mt-1">Stock por unidad · entradas, salidas, merma y costo promedio.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormItem(emptyItem); setDialogItemOpen(true); }} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-nuevo-item">
          <Plus className="w-4 h-4 mr-2" /> Nuevo item
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-stone-200"><CardContent className="p-4"><p className="text-xs text-stone-500 uppercase">Items</p><p className="font-display text-2xl text-stone-900 mt-1">{resumen?.total_items ?? items.length}</p></CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-4"><p className="text-xs text-stone-500 uppercase">Valor stock</p><p className="font-display text-2xl text-emerald-800 mt-1">{fmt(resumen?.valor_total)}</p></CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4"><p className="text-xs text-stone-500 uppercase">Bajo stock</p><p className="font-display text-2xl text-amber-700 mt-1">{resumen?.bajo_stock ?? lowStock.length}</p></CardContent></Card>
        <Card className="border-blue-200"><CardContent className="p-4"><p className="text-xs text-stone-500 uppercase">Movs. mes</p><p className="font-display text-2xl text-blue-700 mt-1">
          {resumen ? ((resumen.movimientos_mes?.entrada || 0) + (resumen.movimientos_mes?.salida || 0) + (resumen.movimientos_mes?.merma || 0) + (resumen.movimientos_mes?.ajuste || 0)) : 0}
        </p></CardContent></Card>
      </div>

      {lowStock.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="alerta-bajo-stock">
            <AlertTriangle className="w-4 h-4" />
            <span><strong>{lowStock.length}</strong> item{lowStock.length !== 1 ? "s" : ""} bajo el stock mínimo — requieren reposición.</span>
          </CardContent>
        </Card>
      )}

      <Card className="border-stone-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-stone-500 text-sm">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Package className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p className="text-sm">Sin items en inventario. Crea el primero con &quot;Nuevo item&quot;.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone-500 text-xs border-b border-stone-200">
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">Categoría</th>
                    <th className="px-4 py-3">Ubicación</th>
                    <th className="px-4 py-3 text-right">Stock</th>
                    <th className="px-4 py-3 text-right">Mín</th>
                    <th className="px-4 py-3 text-right">Costo prom.</th>
                    <th className="px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const bajo = (it.stock_actual || 0) <= (it.stock_minimo || 0);
                    return (
                      <tr key={it.item_id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`row-item-${it.item_id}`}>
                        <td className="px-4 py-2">
                          <p className="font-medium text-stone-900">{it.nombre}</p>
                          <p className="text-xs text-stone-500">{it.unidad}</p>
                        </td>
                        <td className="px-4 py-2 text-stone-600 capitalize">{it.categoria}</td>
                        <td className="px-4 py-2 text-stone-600 text-xs">{ubiName(it.ubicacion_id)}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${bajo ? "text-amber-700" : "text-stone-900"}`}>
                          {it.stock_actual}{bajo && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                        </td>
                        <td className="px-4 py-2 text-right text-stone-500">{it.stock_minimo}</td>
                        <td className="px-4 py-2 text-right text-stone-700">{fmt(it.costo_promedio)}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openMov(it, "entrada")} title="Entrada" data-testid={`btn-entrada-${it.item_id}`}><PlusCircle className="w-4 h-4 text-emerald-700" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openMov(it, "salida")} title="Salida" data-testid={`btn-salida-${it.item_id}`}><MinusCircle className="w-4 h-4 text-stone-700" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openMov(it, "merma")} title="Merma"><AlertTriangle className="w-4 h-4 text-amber-700" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openHist(it)} title="Historial"><History className="w-4 h-4 text-stone-700" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(it); setFormItem({ ...emptyItem, ...it }); setDialogItemOpen(true); }} data-testid={`btn-edit-item-${it.item_id}`}>Editar</Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteItem(it.item_id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog item */}
      <Dialog open={dialogItemOpen} onOpenChange={setDialogItemOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-item">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar item" : "Nuevo item"}</DialogTitle>
            <DialogDescription className="text-xs">Se asigna automáticamente a la unidad actual.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <Input value={formItem.nombre} onChange={(e) => setFormItem({ ...formItem, nombre: e.target.value })} data-testid="item-nombre" />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={formItem.categoria} onValueChange={(v) => setFormItem({ ...formItem, categoria: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unidad</Label>
              <Select value={formItem.unidad} onValueChange={(v) => setFormItem({ ...formItem, unidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNIDADES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stock actual</Label>
              <Input type="number" step="0.01" value={formItem.stock_actual} onChange={(e) => setFormItem({ ...formItem, stock_actual: e.target.value })} data-testid="item-stock-actual" />
            </div>
            <div>
              <Label>Stock mínimo</Label>
              <Input type="number" step="0.01" value={formItem.stock_minimo} onChange={(e) => setFormItem({ ...formItem, stock_minimo: e.target.value })} data-testid="item-stock-minimo" />
            </div>
            <div className="col-span-2">
              <Label>Costo promedio</Label>
              <Input type="number" step="0.01" value={formItem.costo_promedio} onChange={(e) => setFormItem({ ...formItem, costo_promedio: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Ubicación</Label>
              <Select value={formItem.ubicacion_id || "__none__"} onValueChange={(v) => setFormItem({ ...formItem, ubicacion_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {ubicaciones.map((u) => <SelectItem key={u.ubicacion_id} value={u.ubicacion_id}>{u.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea rows={2} value={formItem.notas || ""} onChange={(e) => setFormItem({ ...formItem, notas: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogItemOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveItem} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-guardar-item">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog movimiento */}
      <Dialog open={dialogMovOpen} onOpenChange={setDialogMovOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-movimiento">
          <DialogHeader>
            <DialogTitle>Registrar movimiento</DialogTitle>
            <DialogDescription className="text-xs">{itemForMov?.nombre} · stock: {itemForMov?.stock_actual} {itemForMov?.unidad}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={formMov.tipo} onValueChange={(v) => setFormMov({ ...formMov, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_MOV.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cantidad *</Label><Input type="number" step="0.01" value={formMov.cantidad} onChange={(e) => setFormMov({ ...formMov, cantidad: e.target.value })} data-testid="mov-cantidad" /></div>
              <div><Label>Costo unit.</Label><Input type="number" step="0.01" value={formMov.costo_unitario} onChange={(e) => setFormMov({ ...formMov, costo_unitario: e.target.value })} /></div>
            </div>
            <div><Label>Fecha</Label><Input type="date" value={formMov.fecha} onChange={(e) => setFormMov({ ...formMov, fecha: e.target.value })} /></div>
            <div><Label>Motivo</Label><Input value={formMov.motivo} onChange={(e) => setFormMov({ ...formMov, motivo: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogMovOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveMov} style={{ background: color }} className="text-white hover:opacity-90" data-testid="btn-guardar-mov">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog historial */}
      <Dialog open={dialogHistOpen} onOpenChange={setDialogHistOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historial de movimientos</DialogTitle>
            <DialogDescription className="text-xs">{histItem?.nombre}</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {(histItem?.movimientos_recientes || []).length === 0 ? (
              <p className="text-stone-500 text-sm py-4">Sin movimientos.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-stone-500 text-xs border-b">
                  <th className="py-2">Fecha</th><th className="py-2">Tipo</th><th className="py-2 text-right">Cantidad</th><th className="py-2 text-right">Costo</th><th className="py-2">Motivo</th>
                </tr></thead>
                <tbody>
                  {histItem.movimientos_recientes.map((m) => (
                    <tr key={m.movimiento_id} className="border-b border-stone-100">
                      <td className="py-2 text-stone-600 text-xs">{m.fecha}</td>
                      <td className="py-2 capitalize">{m.tipo}</td>
                      <td className={`py-2 text-right font-medium ${m.tipo === "merma" ? "text-amber-700" : m.tipo === "entrada" ? "text-emerald-700" : "text-stone-700"}`}>
                        {m.tipo === "salida" || m.tipo === "merma" ? "-" : "+"}{m.cantidad}
                      </td>
                      <td className="py-2 text-right text-stone-600">{fmt(m.costo_unitario)}</td>
                      <td className="py-2 text-stone-500 text-xs">{m.motivo || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
